import { Helmet } from 'react-helmet-async';

export const SITE_NAME = 'WigConnection';
export const SITE_URL = 'https://wigconnection.com';
const DEFAULT_DESCRIPTION =
  'Buy, sell, and trade premium brand wigs — Belle Tress, Jon Renau, Ellen Wille, Raquel Welch and more — in a secure marketplace for wig enthusiasts.';
const DEFAULT_IMAGE = `${SITE_URL}/hero-abstract.png`;

interface SEOProps {
  title?: string;
  description?: string;
  /** Path (e.g. "/brand/belle-tress") or absolute URL for the canonical/OG url. */
  canonical?: string;
  image?: string;
  /** OG type — "website" (default) or "product". */
  type?: 'website' | 'product' | 'article';
  noindex?: boolean;
  /** Any JSON-LD objects to embed (Product, BreadcrumbList, etc.). */
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
}

function absolute(url?: string): string | undefined {
  if (!url) return undefined;
  if (/^https?:\/\//i.test(url)) return url;
  return `${SITE_URL}${url.startsWith('/') ? '' : '/'}${url}`;
}

/**
 * Site-wide Organization structured data. Drop into any page's `jsonLd`
 * (usually the Home page) so search engines understand the brand + its links.
 */
export const organizationJsonLd: Record<string, unknown> = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: SITE_NAME,
  url: SITE_URL,
  logo: `${SITE_URL}/wigconnfavicon.png`,
  description:
    'WigConnection is a member marketplace to buy, sell and trade pre-owned premium wigs. Sell your wig and earn a $15 reward toward a new one at MyHairMail.',
  sameAs: ['https://myhairmail.com/', 'https://clearancewigs.com/'],
  parentOrganization: {
    '@type': 'Organization',
    name: 'MyHairMail LLC',
    url: 'https://myhairmail.com/',
  },
};

/** Build FAQPage structured data from a list of question/answer pairs. */
export function faqPageJsonLd(
  items: { question: string; answer: string }[]
): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items.map((it) => ({
      '@type': 'Question',
      name: it.question,
      acceptedAnswer: { '@type': 'Answer', text: it.answer },
    })),
  };
}

export default function SEO({
  title,
  description,
  canonical,
  image,
  type = 'website',
  noindex = false,
  jsonLd,
}: SEOProps) {
  const fullTitle = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME} - Buy, Sell & Trade Premium Wigs`;
  const desc = description || DEFAULT_DESCRIPTION;
  const url = absolute(canonical) || SITE_URL;
  const img = absolute(image) || DEFAULT_IMAGE;
  const blocks = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      <link rel="canonical" href={url} />
      {noindex && <meta name="robots" content="noindex,nofollow" />}

      {/* Open Graph */}
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={img} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={img} />

      {blocks.map((block, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(block)}
        </script>
      ))}
    </Helmet>
  );
}
